/**
 * Param form validation for the add-tile picker.
 *
 * Each tile type has a params form — this module provides zod-validated
 * parse functions and default values per type. Validation matches the
 * canonical param schemas from briefpane/schema exactly.
 *
 * Used by the AddTileDrawer component; results feed parseTile before addTile.
 */
import { z } from 'zod';
import {
  weatherParamsSchema,
  marketsParamsSchema,
  newsParamsSchema,
  sportsParamsSchema,
  clockParamsSchema,
  calendarParamsSchema,
  noteParamsSchema,
  countdownParamsSchema,
  rssParamsSchema,
  isSafeUserSourceUrl,
  NEWS_TOPICS,
  SPORTS_LEAGUES,
  MAX_MARKET_SYMBOLS,
  MAX_CALENDAR_EVENTS,
  MAX_CALENDAR_LOOKAHEAD_HOURS,
  MAX_NOTE_CHARS,
  MAX_COUNTDOWN_LABEL_CHARS,
  MAX_RSS_ITEMS,
  SYMBOL_PATTERN,
  type WeatherParams,
  type MarketsParams,
  type NewsParams,
  type SportsParams,
  type ClockParams,
  type CalendarParams,
  type NoteParams,
  type CountdownParams,
  type RssParams,
} from 'briefpane/schema';

// Re-export for convenience in form components
export {
  NEWS_TOPICS,
  SPORTS_LEAGUES,
  MAX_CALENDAR_EVENTS,
  MAX_CALENDAR_LOOKAHEAD_HOURS,
  MAX_NOTE_CHARS,
  MAX_COUNTDOWN_LABEL_CHARS,
  MAX_RSS_ITEMS,
};

/* ---- Weather ---- */

export interface WeatherFormValues {
  useCurrentLocation: boolean;
  lat: string;
  lon: string;
  units: 'metric' | 'imperial';
}

export const weatherFormDefaults: WeatherFormValues = {
  useCurrentLocation: true,
  lat: '',
  lon: '',
  units: 'imperial',
};

export type FormResult<T> =
  | { ok: true; params: T }
  | { ok: false; errors: Record<string, string> };

const latSchema = z.coerce.number().min(-90).max(90);
const lonSchema = z.coerce.number().min(-180).max(180);

export function validateWeatherForm(
  values: WeatherFormValues,
  geoLat?: number,
  geoLon?: number,
): FormResult<WeatherParams> {
  const lat = values.useCurrentLocation && geoLat !== undefined ? geoLat : parseFloat(values.lat);
  const lon = values.useCurrentLocation && geoLon !== undefined ? geoLon : parseFloat(values.lon);
  const errors: Record<string, string> = {};

  if (!values.useCurrentLocation || geoLat === undefined) {
    const latResult = latSchema.safeParse(values.lat);
    if (!latResult.success) errors['lat'] = 'Enter a latitude between -90 and 90';
    const lonResult = lonSchema.safeParse(values.lon);
    if (!lonResult.success) errors['lon'] = 'Enter a longitude between -180 and 180';
  }

  if (Object.keys(errors).length > 0) return { ok: false, errors };

  const result = weatherParamsSchema.safeParse({
    lat: values.useCurrentLocation && geoLat !== undefined ? geoLat : lat,
    lon: values.useCurrentLocation && geoLon !== undefined ? geoLon : lon,
    units: values.units,
  });
  if (!result.success) {
    return { ok: false, errors: { _: result.error.issues[0]?.message ?? 'Invalid params' } };
  }
  return { ok: true, params: result.data };
}

/* ---- News ---- */

/**
 * Matches the canonical NEWS_QUERY_PATTERN from briefpane/schema constants.
 * Keep in sync: /^[A-Za-z0-9 .,'&:+\-]+$/, min 2, max 60 chars.
 */
const NEWS_QUERY_FORM_PATTERN = /^[A-Za-z0-9 .,'&:+\-]+$/;

export interface NewsFormValues {
  topic: (typeof NEWS_TOPICS)[number];
  count: string;
  /** Optional keyword filter — empty string means no keyword (use top-headlines). */
  query: string;
}

export const newsFormDefaults: NewsFormValues = {
  topic: 'world',
  count: '5',
  query: '',
};

export function validateNewsForm(values: NewsFormValues): FormResult<NewsParams> {
  const errors: Record<string, string> = {};
  const countNum = parseInt(values.count, 10);
  if (isNaN(countNum) || countNum < 1 || countNum > 8) {
    errors['count'] = 'Enter a number between 1 and 8';
  }
  const trimmedQuery = values.query.trim();
  if (trimmedQuery.length > 0) {
    if (trimmedQuery.length < 2) {
      errors['query'] = 'Keyword must be at least 2 characters';
    } else if (trimmedQuery.length > 60) {
      errors['query'] = 'Keyword is too long (max 60 characters)';
    } else if (!NEWS_QUERY_FORM_PATTERN.test(trimmedQuery)) {
      errors['query'] = "Use letters, numbers, spaces, and basic punctuation (.,\\'&:+-)";
    }
  }
  if (Object.keys(errors).length > 0) return { ok: false, errors };

  const parseInput: Record<string, unknown> = { topic: values.topic, count: countNum };
  if (trimmedQuery.length > 0) parseInput['query'] = trimmedQuery;

  const result = newsParamsSchema.safeParse(parseInput);
  if (!result.success) {
    return { ok: false, errors: { _: result.error.issues[0]?.message ?? 'Invalid params' } };
  }
  return { ok: true, params: result.data };
}

/* ---- Markets ---- */

export interface MarketsFormValues {
  symbolsRaw: string;
  display: 'price' | 'change';
}

export const marketsFormDefaults: MarketsFormValues = {
  symbolsRaw: 'SPY',
  display: 'change',
};

export function validateMarketsForm(values: MarketsFormValues): FormResult<MarketsParams> {
  const errors: Record<string, string> = {};
  const symbols = values.symbolsRaw
    .split(',')
    .map((s) => s.trim().toUpperCase())
    .filter((s) => s.length > 0);

  if (symbols.length === 0) {
    errors['symbolsRaw'] = 'Enter at least one symbol';
  } else if (symbols.length > MAX_MARKET_SYMBOLS) {
    errors['symbolsRaw'] = `Maximum ${MAX_MARKET_SYMBOLS} symbols`;
  } else {
    const bad = symbols.filter((s) => !SYMBOL_PATTERN.test(s));
    if (bad.length > 0) {
      errors['symbolsRaw'] = `Invalid symbol(s): ${bad.join(', ')}`;
    }
  }

  if (Object.keys(errors).length > 0) return { ok: false, errors };

  const result = marketsParamsSchema.safeParse({ symbols, display: values.display });
  if (!result.success) {
    return { ok: false, errors: { _: result.error.issues[0]?.message ?? 'Invalid params' } };
  }
  return { ok: true, params: result.data };
}

/* ---- Sports ---- */

export interface SportsFormValues {
  league: (typeof SPORTS_LEAGUES)[number];
  teamId: string;
}

export const sportsFormDefaults: SportsFormValues = {
  league: 'nfl',
  teamId: '',
};

export function validateSportsForm(values: SportsFormValues): FormResult<SportsParams> {
  const errors: Record<string, string> = {};
  if (!values.teamId || !/^[a-zA-Z0-9_-]{1,32}$/.test(values.teamId)) {
    errors['teamId'] = 'Enter a valid team ID (letters, numbers, hyphens, underscores; max 32)';
  }
  if (Object.keys(errors).length > 0) return { ok: false, errors };

  const result = sportsParamsSchema.safeParse({ league: values.league, teamId: values.teamId });
  if (!result.success) {
    return { ok: false, errors: { _: result.error.issues[0]?.message ?? 'Invalid params' } };
  }
  return { ok: true, params: result.data };
}

/* ---- Clock ---- */

export interface ClockFormValues {
  tz: string;
  format: '12h' | '24h';
}

export const clockFormDefaults: ClockFormValues = {
  tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
  format: '12h',
};

export function validateClockForm(values: ClockFormValues): FormResult<ClockParams> {
  const errors: Record<string, string> = {};
  if (!values.tz || values.tz.length < 1 || values.tz.length > 64) {
    errors['tz'] = 'Enter a timezone (e.g. America/New_York)';
  } else if (!/^[A-Za-z0-9_+\-/]+$/.test(values.tz)) {
    errors['tz'] = 'Invalid timezone format';
  }
  if (Object.keys(errors).length > 0) return { ok: false, errors };

  const result = clockParamsSchema.safeParse({ tz: values.tz, format: values.format });
  if (!result.success) {
    return { ok: false, errors: { _: result.error.issues[0]?.message ?? 'Invalid params' } };
  }
  return { ok: true, params: result.data };
}

/* ---- Calendar ---- */

export interface CalendarFormValues {
  maxEvents: string;
  lookaheadHours: string;
}

export const calendarFormDefaults: CalendarFormValues = {
  maxEvents: '3',
  lookaheadHours: '12',
};

export function validateCalendarForm(values: CalendarFormValues): FormResult<CalendarParams> {
  const errors: Record<string, string> = {};
  const maxEventsNum = parseInt(values.maxEvents, 10);
  if (isNaN(maxEventsNum) || maxEventsNum < 1 || maxEventsNum > MAX_CALENDAR_EVENTS) {
    errors['maxEvents'] = `Enter a number between 1 and ${MAX_CALENDAR_EVENTS}`;
  }
  const lookaheadNum = parseInt(values.lookaheadHours, 10);
  if (isNaN(lookaheadNum) || lookaheadNum < 1 || lookaheadNum > MAX_CALENDAR_LOOKAHEAD_HOURS) {
    errors['lookaheadHours'] = `Enter a number between 1 and ${MAX_CALENDAR_LOOKAHEAD_HOURS}`;
  }
  if (Object.keys(errors).length > 0) return { ok: false, errors };

  const result = calendarParamsSchema.safeParse({
    calendarId: 'primary',
    maxEvents: maxEventsNum,
    lookaheadHours: lookaheadNum,
  });
  if (!result.success) {
    return { ok: false, errors: { _: result.error.issues[0]?.message ?? 'Invalid params' } };
  }
  return { ok: true, params: result.data };
}

/* ---- Note ---- */

export interface NoteFormValues {
  text: string;
}

export const noteFormDefaults: NoteFormValues = {
  text: '',
};

export function validateNoteForm(values: NoteFormValues): FormResult<NoteParams> {
  const errors: Record<string, string> = {};
  if (values.text.length > MAX_NOTE_CHARS) {
    errors['text'] = `Note is too long (max ${MAX_NOTE_CHARS} characters)`;
  }
  if (Object.keys(errors).length > 0) return { ok: false, errors };

  const result = noteParamsSchema.safeParse({ text: values.text });
  if (!result.success) {
    return { ok: false, errors: { _: result.error.issues[0]?.message ?? 'Invalid params' } };
  }
  return { ok: true, params: result.data };
}

/* ---- Todo ---- */

// The todo form is a minimal "confirm to add" — items are added in the detail
// sheet after creation (per spec: "form is just an 'Add' confirm with helper text").
// No user input needed at add time; we create the tile with an empty items list.

export interface TodoFormValues {
  // Intentionally empty at add time: items are added via TodoDetail after creation.
  _placeholder?: undefined;
}

export const todoFormDefaults: TodoFormValues = {};

export function validateTodoForm(_values: TodoFormValues): FormResult<{ items: never[] }> {
  // Always valid: fresh tile starts with empty list per spec.
  return { ok: true, params: { items: [] } };
}

/* ---- Countdown ---- */

export interface CountdownFormValues {
  label: string;
  // datetime-local input string (e.g. "2026-08-01T09:00") — converted to ISO at submit
  targetLocal: string;
}

/**
 * Build the default +7d datetime-local string (browser local time, no timezone).
 * datetime-local inputs use "YYYY-MM-DDTHH:MM" format without timezone.
 */
export function countdownDefaultTargetLocal(): string {
  const d = new Date(Date.now() + 7 * 24 * 3600 * 1000);
  // Format: YYYY-MM-DDTHH:MM (drop seconds + zone for datetime-local input)
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function countdownFormDefaults(): CountdownFormValues {
  return {
    label: 'Launch day',
    targetLocal: countdownDefaultTargetLocal(),
  };
}

export function validateCountdownForm(values: CountdownFormValues): FormResult<CountdownParams> {
  const errors: Record<string, string> = {};

  if (!values.label || values.label.trim().length === 0) {
    errors['label'] = 'Enter a label for the event';
  } else if (values.label.length > MAX_COUNTDOWN_LABEL_CHARS) {
    errors['label'] = `Label is too long (max ${MAX_COUNTDOWN_LABEL_CHARS} characters)`;
  }

  if (!values.targetLocal) {
    errors['targetLocal'] = 'Pick a target date and time';
  }

  if (Object.keys(errors).length > 0) return { ok: false, errors };

  // Convert datetime-local to ISO with timezone offset.
  // new Date(datetimeLocal) in most environments treats no-tz strings as local time.
  const d = new Date(values.targetLocal);
  if (isNaN(d.getTime())) {
    return { ok: false, errors: { targetLocal: 'Enter a valid date and time' } };
  }
  const targetISO = d.toISOString();

  const result = countdownParamsSchema.safeParse({ label: values.label.trim(), targetISO });
  if (!result.success) {
    return { ok: false, errors: { _: result.error.issues[0]?.message ?? 'Invalid params' } };
  }
  return { ok: true, params: result.data };
}

/* ---- RSS Feed ---- */

/**
 * Per-failure calm messages for userSourceUrlSchema violations.
 * Keyed to the specific constraint that fired — surfaced live as the user types.
 * ADR-005: feedUrl is treated as private; no analytics event captures it.
 */
export function rssUrlErrorMessage(raw: string): string | null {
  if (!raw || raw.trim().length === 0) return null; // empty — show nothing until user types

  const trimmed = raw.trim();
  if (/\s/.test(trimmed)) return 'Remove spaces from the URL';

  // Must start with https://
  if (trimmed.startsWith('http://')) return 'https only — http feeds are not supported';
  if (!trimmed.startsWith('https://')) return 'Enter a URL starting with https://';

  let url: URL;
  try {
    url = new URL(trimmed);
  } catch {
    return 'Enter a valid URL (e.g. https://example.com/feed)';
  }

  if (url.username !== '' || url.password !== '') {
    return 'Credentials are not allowed in feed URLs';
  }
  if (url.port !== '') {
    return 'Non-standard ports are not supported — use the default https port (443)';
  }

  // Kept in sync with isLocalhostFamily in user-source-url.ts (incl. the
  // trailing-dot strip from review HIGH-2 and the .internal suffix). The
  // schema remains the authoritative gate; this only shapes the live message.
  const h = url.hostname.toLowerCase().replace(/\.$/, '');
  if (
    h === 'localhost' || h === '127.0.0.1' || h === '::1' ||
    h.endsWith('.local') || h.endsWith('.localhost') || h.endsWith('.internal')
  ) {
    return 'That address looks private — use a public feed URL';
  }
  // Private IPv4: 10.x, 172.16-31.x, 192.168.x, 169.254.x, 0.x
  if (/^(10\.|172\.(1[6-9]|2\d|3[01])\.|192\.168\.|169\.254\.|0\.)/.test(h)) {
    return 'That address looks private — use a public feed URL';
  }
  if (trimmed.includes('[') || trimmed.includes(']')) {
    return 'IPv6 addresses are not supported — use a hostname';
  }
  if (trimmed.length > 300) {
    return 'URL is too long (max 300 characters)';
  }

  // All checks passed — the URL looks valid
  return null;
}

export interface RssFormValues {
  feedUrl: string;
  maxItems: string;
}

export const rssFormDefaults: RssFormValues = {
  feedUrl: '',
  maxItems: '5',
};

/**
 * Validate the RSS add-tile form.
 *
 * feedUrl must pass `userSourceUrlSchema` constraints (https, no credentials, no port,
 * no private hosts). Empty URL yields a specific error, not a generic one.
 *
 * ADR-005 PRIVACY: feedUrl never flows to an analytics event property.
 * This is enforced at the call sites (AddTileDrawer passes {type} only to tile_added).
 */
export function validateRssForm(values: RssFormValues): FormResult<RssParams> {
  const errors: Record<string, string> = {};

  if (!values.feedUrl || values.feedUrl.trim().length === 0) {
    errors['feedUrl'] = 'Enter a feed URL (e.g. https://example.com/rss)';
  } else {
    const urlErr = rssUrlErrorMessage(values.feedUrl);
    if (urlErr !== null) {
      errors['feedUrl'] = urlErr;
    } else if (!isSafeUserSourceUrl(values.feedUrl.trim())) {
      // Catch-all: schema validation found something our message map missed
      errors['feedUrl'] = 'Enter a valid public https feed URL';
    }
  }

  const maxItemsNum = parseInt(values.maxItems, 10);
  if (isNaN(maxItemsNum) || maxItemsNum < 1 || maxItemsNum > MAX_RSS_ITEMS) {
    errors['maxItems'] = `Enter a number between 1 and ${MAX_RSS_ITEMS}`;
  }

  if (Object.keys(errors).length > 0) return { ok: false, errors };

  const result = rssParamsSchema.safeParse({
    feedUrl: values.feedUrl.trim(),
    maxItems: maxItemsNum,
  });
  if (!result.success) {
    return { ok: false, errors: { _: result.error.issues[0]?.message ?? 'Invalid params' } };
  }
  return { ok: true, params: result.data };
}

/* ---- Geo suggestions (used by WeatherForm city search) ---- */

/** One result from the /api/geo endpoint — matches GeoResultItem in geo.ts. */
export interface GeoSuggestion {
  readonly name: string;
  readonly state?: string;
  readonly country: string;
  readonly lat: number;
  readonly lon: number;
}
